<script lang="ts" generics="TValues">
    import MobileTypeInput from './MobileTypeInput.svelte';
    import type { Field, Form } from '@jmvdevelop/svelte-form';
    import type { SearchHeaderFormData } from '$components/SearchHeader/SearchHeaderFormData';
    import { query } from '$lib/apollo/SvelteApollo';
    import { WhatWhoQueryDocument, type WhatWhoQueryQuery } from '$gql/graphql';
    import WhatWhoOption from '$components/SearchHeader/WhatWhoOption.svelte';
    import { type ComboboxOptionProps } from '@melt-ui/svelte';
    import type { IterableElement } from 'type-fest';
    import { match, P } from 'ts-pattern';
    import SearchHeaderComboxbox from '$components/SearchHeader/SearchHeaderComboxbox.svelte';
    import MobileModalCombobox, {
        createDialog,
    } from '$components/SearchHeader/MobileModalCombobox.svelte';
    import MobileButton from '$components/SearchHeader/MobileButton.svelte';
    import StackZIndex from '$components/ZIndex/StackZIndex.svelte';
    import TypeInput from '$components/SearchHeader/TypeInput.svelte';
    import { readable } from 'svelte/store';
    import * as t from '$trans';
    import { getContainer } from '$components/Context/ContainerContext';

    export let form: Form<TValues, string>;
    export let queryField: Field<TValues, string | null>;
    export let itemField: Field<TValues, SearchHeaderFormData['whatWhoItem'] | null>;
    export let typeField: Field<TValues, SearchHeaderFormData['type']>;
    export let withTypeSelect: boolean = false; // True means we add the search type selector to the left of the input
    export let style: 'desktop' | 'desktop-full' | 'mobile';

    const { lang } = getContainer();

    const q = form.stores().fieldValue(queryField);
    const type = form.stores().fieldValue(typeField);

    let skip = true;
    $: response = skip
        ? { result: readable(null), setVariables: null }
        : query(WhatWhoQueryDocument, {
              variables: { q: $q ?? '', type: $type },
          });
    $: ({ result, setVariables } = response);
    $: setVariables?.({ q: $q ?? '', type: $type });
    $: items =
        ($q ?? '').trim() === '' ? [] : ($result?.data?.searchHeader.whatWho ?? []);

    $: placeholder = match($type)
        .with('company', () => t.search_header.placeholder_whatwho_company({}, $lang))
        .with('pro', () => t.search_header.placeholder_whatwho_pro({}, $lang))
        .with('newsfeed', () => t.search_header.placeholder_whatwho_newsfeed({}, $lang))
        .with('job', () => t.search_header.placeholder_whatwho_job({}, $lang))
        .exhaustive();
    $: label = match($type)
        .with('company', () => t.search_header.label_whatwho_company({}, $lang))
        .with('pro', () => t.search_header.label_whatwho_pro({}, $lang))
        .with('newsfeed', () => t.search_header.label_whatwho_newsfeed({}, $lang))
        .with('job', () => t.search_header.label_whatwho_job({}, $lang))
        .exhaustive();

    function toOption(
        item: IterableElement<WhatWhoQueryQuery['searchHeader']['whatWho']>,
    ): ComboboxOptionProps<SearchHeaderFormData['whatWhoItem']> {
        return match(item)
            .with(
                {
                    user: {
                        __typename: 'User',
                        fullname: P.select('fullname'),
                        id: P.select('id'),
                    },
                },
                ({ id, fullname }) =>
                    ({
                        value: { userId: id },
                        label: fullname ?? '',
                    }) as const,
            )
            .with(
                {
                    company: {
                        __typename: 'Company',
                        name: P.select('name'),
                        id: P.select('id'),
                    },
                },
                ({ id, name }) =>
                    ({
                        value: { companyId: id },
                        label: name,
                    }) as const,
            )
            .with(
                {
                    category: {
                        __typename: 'Category',
                        name: P.select('name'),
                        id: P.select('id'),
                    },
                },
                ({ id, name }) =>
                    ({
                        value: { categoryId: id },
                        label: name,
                    }) as const,
            )
            .with(
                {
                    shoppingProduct: {
                        __typename: 'ShoppingProduct',
                        name: P.select('name'),
                        id: P.select('id'),
                    },
                },
                ({ id, name }) =>
                    ({
                        value: { shoppingProductId: id },
                        label: name,
                    }) as const,
            )
            .with(
                {
                    jobOffer: {
                        __typename: 'JobOffer',
                        title: P.select('title'),
                        id: P.select('id'),
                    },
                },
                ({ id, title }) =>
                    ({
                        value: { jobOfferId: id },
                        label: title,
                    }) as const,
            )
            .with(
                {
                    text: P.string.select('text'),
                },
                ({ text }) =>
                    ({
                        value: { text },
                        label: text,
                    }) as const,
            )
            .otherwise(() => ({
                value: null,
                label: '',
            }));
    }

    const giveFocusToField = (event: MouseEvent) => {
        if (event.target instanceof HTMLElement) {
            if (event.target.tagName === 'DIV') {
                const input = document.getElementById(form.getFieldId(queryField));
                input?.click();
                input?.focus();
            }
        }
    };

    const mobileDialog = createDialog(form, queryField, {});

    function onItemCanBeLoaded() {
        skip = false;
    }
</script>

{#if style === 'mobile'}
    <div class="flex">
        {#if withTypeSelect}
            <div class="pl-[20px]">
                <MobileTypeInput {form} field={typeField} variant="just-icon" />
            </div>
        {/if}

        <div class="flex-auto">
            <MobileButton
                {form}
                {queryField}
                {itemField}
                {label}
                {placeholder}
                dialog={mobileDialog}
                withSearch={false}
            />
        </div>

        <MobileModalCombobox
            {form}
            {queryField}
            {itemField}
            {items}
            {toOption}
            dialog={mobileDialog}
            {label}
            {placeholder}
            {onItemCanBeLoaded}
        >
            <svelte:fragment slot="item" let:item>
                <WhatWhoOption option={item} query={$q} class="b:px-0" />
            </svelte:fragment>
        </MobileModalCombobox>
    </div>
{:else}
    <!-- svelte-ignore a11y-no-static-element-interactions a11y-click-events-have-key-events -->
    <div
        class="h-[64px] rounded-l-[50px] bg-white flex-1 cursor-pointer flex"
        on:click|trusted={giveFocusToField}
    >
        {#if withTypeSelect}
            <div class="pl-[12px] pr-[1px]">
                <TypeInput {form} field={typeField} variant="rounded-icon" />
            </div>
        {/if}

        <StackZIndex zIndex="current" let:zIndex>
            <SearchHeaderComboxbox
                {form}
                {queryField}
                {itemField}
                {items}
                {toOption}
                {label}
                {placeholder}
                zIndex={zIndex - 1}
                thinLeftPad={withTypeSelect}
                {onItemCanBeLoaded}
            >
                <svelte:fragment slot="item" let:item>
                    <WhatWhoOption option={item} query={$q} />
                </svelte:fragment>
            </SearchHeaderComboxbox>
        </StackZIndex>
    </div>
{/if}
